import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useQueryParam from '@jwp/ott-ui-react/src/hooks/useQueryParam';
import { useQuery } from '@apollo/client/react';
import { useUploadStore } from '@jwp/ott-common/src/stores/UploadStore';
import { GET_MY_VIDEOS, GetMyVideosData, GetMyVideosVariables, MediaCategoryReels } from '@jwp/ott-common/src/graphql/query/reels.queries';
import env from '@jwp/ott-common/src/env';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import { GET_DROPDOWNS, GetDropdownsVariables, GetDropdownsData } from '@jwp/ott-common/src/graphql/query/broadcastLive.queries';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import VideoPreview from '../../components/VideoPreview/VideoPreview';
import {
  GET_MY_CHANNEL, type GetMyChannelData,
  GET_CHANNEL_VIDEOS,
  GetChannelVideosData,
  GetChannelVideosVariables,
} from '@jwp/ott-common/src/graphql/query/channel.queries';

const CHANNEL_REELS_PAGE_SIZE = 12;
const OFFSET_STEP = 10;

const VideoPreviewPage = () => {
  const navigate = useNavigate();
  const { id, chId } = useParams<{ id: string; chId: string }>();
  const { file, previewUrl, values, videoValues, returnPath } = useUploadStore();
  const urlReturnPath = useQueryParam('returnPath');
  const typeParam = useQueryParam('type');
  const offsetParam = useQueryParam('offset');
  const effectiveReturnPath = returnPath || urlReturnPath;

  const initialOffset = offsetParam ? parseInt(offsetParam, 10) : 0;

  const { data: channelData, loading: channelLoading } = useQuery<GetMyChannelData>(GET_MY_CHANNEL, {
    client: getAuthApolloClient(),
    fetchPolicy: 'network-only',
    variables: chId ? { chId: chId } : {}
  });

  const apiHandle = channelData?.getMyChannel?.channels?.[0]?.chId || ''

  const isVideoType = typeParam === 'video';
  const isReel = !isVideoType;


  const queryVariables = useMemo(
    () => ({
      filter: { maId: { equalTo: id || '' } },
      first: 1,
      offset: 0,
    }),
    [id],
  );

  const { data: singleData, loading: singleLoading } = useQuery<GetMyVideosData, GetMyVideosVariables>(GET_MY_VIDEOS, {
    variables: queryVariables,
    skip: !id || !isVideoType,
  });

  const getChannelVideosVariables = (maType: 'reels' | 'videos', offset = 0): GetChannelVideosVariables => ({
    input: {
      first: 12,
      offset,
      maType,
      maUploadStatus: 'uploaded',
      chId: apiHandle ? [apiHandle] : [],
    },
  });

  const { data, loading, error, fetchMore } = useQuery<GetChannelVideosData, GetChannelVideosVariables>(GET_CHANNEL_VIDEOS, {
    client: getAuthApolloClient(),
    variables: getChannelVideosVariables('reels', initialOffset),
    skip: !apiHandle || isVideoType,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const { data: dropdownData } = useQuery<GetDropdownsData, GetDropdownsVariables>(GET_DROPDOWNS, {
    client: getAuthApolloClient(),
    variables: { ddType: 'category' },
    errorPolicy: 'all',
  });

  const categories: MediaCategoryReels[] = useMemo(() => dropdownData?.getDropdowns?.dropdowns || [], [dropdownData]);

  const [channelReels, setChannelReels] = useState<any[]>([]);
  const [baseOffset, setBaseOffset] = useState(initialOffset); // channelReels[0] corresponds to this global offset
  const [hasMoreReels, setHasMoreReels] = useState(true);          // more data AFTER loaded window
  const [hasMorePrevReels, setHasMorePrevReels] = useState(initialOffset > 0); // more data BEFORE loaded window
  const [loadingMoreReels, setLoadingMoreReels] = useState(false);
  const [loadingPrevReels, setLoadingPrevReels] = useState(false);
  const [currentReelIndex, setCurrentReelIndex] = useState(0);
  const [hasSyncedInitialReel, setHasSyncedInitialReel] = useState(false);
  const lastSyncedReelIdRef = useRef<string | undefined>(id);

  const totalCount = data?.getMyVideos?.totalCount || 0;
  useEffect(() => {
    if (!isReel) { return; }
    const videos = data?.getMyVideos?.videos;
    if (!videos) { return; }

    setChannelReels(videos);
    setBaseOffset(initialOffset);
    const loadedUpTo = initialOffset + videos.length;
    setHasMoreReels(loadedUpTo < totalCount);
    setHasMorePrevReels(initialOffset > 0);
  }, [data, isReel, initialOffset, totalCount]);


  useEffect(() => {
    if (hasSyncedInitialReel) { return; }
    if (!channelReels.length) { return; }

    if (!id) {
      setHasSyncedInitialReel(true);
      return;
    }

    const index = channelReels.findIndex((reel: any) => reel.maId === id);
    if (index < 0) { return; } // not on this page yet (still loading / different channel)

    setHasSyncedInitialReel(true);
    if (index !== currentReelIndex) {
      setCurrentReelIndex(index);
    }
  }, [channelReels, id, hasSyncedInitialReel, currentReelIndex]);

  const handleLoadMoreReels = useCallback(async () => {
    if (!isReel || !apiHandle || loadingMoreReels || !hasMoreReels) { return; }

    try {
      setLoadingMoreReels(true);
      const nextOffset = baseOffset + channelReels.length; // <-- baseOffset added
      const res = await fetchMore({
        variables: getChannelVideosVariables('reels', nextOffset),
      });

      const newVideos = res.data?.getMyVideos?.videos || [];
      const newTotalCount = res.data?.getMyVideos?.totalCount || 0;
      setChannelReels((prev) => {
        const existingIds = new Set(prev.map((n: any) => n.maId));
        return [...prev, ...newVideos.filter((n: any) => !existingIds.has(n.maId))];
      });
      const newLoadedUpTo = nextOffset + newVideos.length;
      setHasMoreReels(newLoadedUpTo < newTotalCount);
      // setHasMoreReels(newVideos.length >= CHANNEL_REELS_PAGE_SIZE);
    } catch (err) {
      console.error('Failed to load more reels:', err);
    } finally {
      setLoadingMoreReels(false);
    }
  }, [isReel, apiHandle, loadingMoreReels, hasMoreReels, fetchMore, channelReels.length, baseOffset]);

  const handleLoadPrevReels = useCallback(async () => {
    if (!isReel || !apiHandle || loadingPrevReels || !hasMorePrevReels || baseOffset <= 0) { return; }

    try {
      setLoadingPrevReels(true);
      const prevOffset = Math.max(0, baseOffset - OFFSET_STEP);

      const res = await fetchMore({
        variables: getChannelVideosVariables('reels', prevOffset),
      });
      const prevVideos = res.data?.getMyVideos?.videos || [];

      setChannelReels((prev) => {
        const existingIds = new Set(prev.map((n: any) => n.maId));
        const newOnes = prevVideos.filter((n: any) => !existingIds.has(n.maId));

        if (newOnes.length) {
          // user was at index 0 (viewing item that starts at baseOffset);
          // land them on the item right before it, in the newly-prepended batch
          setCurrentReelIndex((idx) => idx + newOnes.length - 1);
        } else {
          setHasMorePrevReels(false); // nothing new came back, avoid refetch loop
        }

        return [...newOnes, ...prev];
      });

      setBaseOffset(prevOffset);
      if (prevOffset <= 0) { setHasMorePrevReels(false); }
    } catch (err) {
      console.error('Failed to load previous reels:', err);
    } finally {
      setLoadingPrevReels(false);
    }
  }, [isReel, apiHandle, loadingPrevReels, hasMorePrevReels, baseOffset, fetchMore]);

  const canGoPrevReel = isReel && (currentReelIndex > 0 || hasMorePrevReels);
  const canGoNextReel = isReel && (currentReelIndex < channelReels.length - 1 || hasMoreReels);

  const handlePrevReel = useCallback(() => {
    if (!isReel) { return; }

    if (currentReelIndex > 0) {
      setCurrentReelIndex((prev) => prev - 1);
      return;
    }

    if (hasMorePrevReels) {
      handleLoadPrevReels();
    }
  }, [isReel, currentReelIndex, hasMorePrevReels, handleLoadPrevReels]);

  const handleNextReel = useCallback(() => {
    if (!isReel) { return; }
    const nextIndex = currentReelIndex + 1;
    if (currentReelIndex >= channelReels.length - 1) {
      if (hasMoreReels) {
        handleLoadMoreReels();
        setCurrentReelIndex(nextIndex);
      }
      return; // nothing to move to yet — arrow re-enables once the fetch above lands
    }
    // fetch the next page 2 items early so the arrow never stalls waiting on a request
    const itemsUntilEnd = channelReels.length - nextIndex;
    if (itemsUntilEnd <= 2 && hasMoreReels) {
      handleLoadMoreReels();
    }
    setCurrentReelIndex(nextIndex);
  }, [isReel, currentReelIndex, channelReels.length, hasMoreReels, handleLoadMoreReels]);

  // Keep the URL in sync with whichever reel is showing, without a router navigation
  // (which would re-trigger queries on every arrow press).
  useEffect(() => {
    if (!isReel) { return; }
    const activeReel = channelReels[currentReelIndex];
    if (!activeReel?.maId || activeReel.maId === lastSyncedReelIdRef.current) { return; }

    const newPath = window.location.pathname.replace(lastSyncedReelIdRef.current || '', activeReel.maId);
    window.history.replaceState(null, '', `${newPath}${window.location.search}`);
    lastSyncedReelIdRef.current = activeReel.maId;
  }, [currentReelIndex, channelReels, isReel]);

  // The video actually being displayed: reels come from the playlist, single videos
  // come from the direct-by-id query.
  const singleVideoData = singleData?.allMediaApprovals?.nodes?.[0];
  const videoData = isReel ? channelReels[currentReelIndex] : singleVideoData;

  const effectiveTitle = useMemo(() => {
    if (id && videoData) { return videoData.maTitle; }
    return isVideoType ? videoValues.title : values.title;
  }, [id, videoData, isVideoType, videoValues.title, values.title]);

  const effectiveDescription = useMemo(() => {
    if (id && videoData) { return videoData.maDescription; }
    return isVideoType ? videoValues.description : values.description;
  }, [id, videoData, isVideoType, videoValues.description, values.description]);

  const handleCategory = (categoryId: string) =>
    categories.find((item) => item.ddId === categoryId)?.ddLabel;

  const effectiveCategory = useMemo(() => {
    if (id && videoData) {
      return handleCategory(videoData.maCategory);
    }

    return isVideoType ? undefined : handleCategory(values.category);
  }, [id, videoData, isVideoType, values.category, categories]);

  const effectiveAuthor = useMemo(() => {
    if (id && videoData) {
      return {
        name: videoData.userByMaUploadedBy?.uFullName || videoData.uploadedUserInfo?.uFullName || null,
        avatarUrl: videoData.userByMaUploadedBy?.uAvatarUrl || videoData.uploadedUserInfo?.uAvatarUrl || null,
      };
    }
    return undefined;
  }, [id, videoData]);

  const effectiveChannelInfo = useMemo(() => {
    if (id && videoData) {
      return videoData.channelInfo;
    }
    return undefined;
  }, [id, videoData]);

  const effectiveItem = useMemo(() => {
    if (id && videoData) { return videoData as any; }

    // Minimal item shape for preview/new uploads so UI components that read
    // item fields (e.g. CollapsibleText) don't crash when item is missing.
    return {
      audio_language: undefined,
      sub_title: undefined,
      cast: undefined,
      director: undefined,
      producer: undefined,
      studio: undefined,
      // include title/description for any callers that might read them
      title: isVideoType ? videoValues.title : values.title,
      description: isVideoType ? videoValues.description : values.description,
    } as any;
  }, [id, videoData, isVideoType, videoValues.title, videoValues.description, values.title, values.description]);

  // const isReel = videoData ? videoData.maType === 'REELS' : typeParam !== 'video';


  const handleReportSuccess = useCallback(() => {
    if (!isReel) { return; } // video single-item view ku report-remove logic thevai illa

    const reportedMaId = videoData?.maId;
    if (!reportedMaId) { return; }

    const reportedIndex = currentReelIndex;

    setChannelReels((prev) => {
      const filtered = prev.filter((item: any) => item.maId !== reportedMaId);

      // Playlist completely empty — vera data illa, close pannitu return pannunga
      if (filtered.length === 0) {
        if (effectiveReturnPath) {
          navigate(effectiveReturnPath);
        } else {
          navigate(-1);
        }
        return filtered;
      }

      // Next item automatic ah "reportedIndex" position ku shift aagidum.
      // Adhu illana (last item ah report pannitanga na), previous item ku poganum.
      const nextIndex = reportedIndex < filtered.length ? reportedIndex : filtered.length - 1;
      setCurrentReelIndex(nextIndex);

      return filtered;
    });
  }, [isReel, videoData?.maId, currentReelIndex, effectiveReturnPath, navigate]);

  useEffect(() => {
    // If not viewing an existing video (no id param) and no file/previewUrl in memory, redirect.
    if (!id && !file && !previewUrl) {
      const fallbackPath = effectiveReturnPath || `${-1}`;
      navigate(fallbackPath, { replace: true });
    }
  }, [id, file, previewUrl, navigate, effectiveReturnPath]);

  if (!id && !file && !previewUrl) {
    return null;
  }

  const isReelReady = !isReel || (hasSyncedInitialReel && !!channelReels[currentReelIndex]);

  const isPageLoading = isVideoType
    ? singleLoading
    : channelLoading || loading || !isReelReady;

  if (isPageLoading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000' }}>
        <LoadingOverlay inline />
      </div>
    );
  }

  const effectiveFile = id ? null : file;
  const effectivePreviewUrl = id && videoData
    ? `${env.APP_BASE_URL || ''}${videoData.maS3MediaKeyUrl}`
    : isReel
      ? undefined
      : previewUrl;

  return (
    <>
      <VideoPreview
        key={id}
        file={effectiveFile}
        previewUrl={effectivePreviewUrl}
        title={effectiveTitle}
        description={effectiveDescription}
        category={effectiveCategory}
        author={effectiveAuthor}
        channelInfo={effectiveChannelInfo}
        item={effectiveItem}
        showDetails={!!id}
        isReel={isReel}
        onNextReel={handleNextReel}
        onPrevReel={handlePrevReel}
        canGoNext={canGoNextReel}
        canGoPrev={canGoPrevReel}
        setChannelReels={setChannelReels}
        handleReportSuccess={handleReportSuccess}
        onClose={() => {
          if (effectiveReturnPath) {
            navigate(effectiveReturnPath);
          } else {
            navigate(-1);
          }
        }}
      />

    </>
  );
};

export default VideoPreviewPage;
